import { appLocalStorage } from '../utils/storage';
import config from '../utils/config';
import { getUser, removeUser } from '../utils/func';
import { cleanupDeletedOwner } from './cleanup';
import { abortable } from '../utils/abortable';

const prefix = 'account-deletion:receipt:';
const latestKey = 'account-deletion:receipt';
const uuid = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i;
const secretPattern = /^[A-Za-z0-9_-]{43}$/;
export const validReceipt = value => Boolean(value && uuid.test(value.request_id) && secretPattern.test(value.receipt_secret));
const base64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
export async function receiptHash(secret) {
  if (!secretPattern.test(secret)) throw new Error('Invalid receipt');
  const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(secret));
  return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, '0')).join('');
}
export function saveReceipt(receipt) {
  if (!validReceipt(receipt)) throw new Error('Invalid receipt');
  const value = JSON.stringify(receipt);
  appLocalStorage.setItem(prefix + receipt.request_id, value);
  if (appLocalStorage.getItem(prefix + receipt.request_id) !== value) throw new Error('Receipt storage unavailable');
  appLocalStorage.setItem(latestKey, receipt.request_id);
  return receipt;
}
export function readReceipt(id) {
  try {
    const receipt = JSON.parse(appLocalStorage.getItem(prefix + (id || appLocalStorage.getItem(latestKey))));
    return validReceipt(receipt) ? receipt : null;
  } catch { return null; }
}
export async function createReceipt(userId) {
  const previous = readReceipt();
  // A retry of an uncertain response reuses its request and proof, never creates
  // another deletion job. A completed owner's receipt is never reused for B.
  if (previous?.owner_user_id === userId && !previous.accepted && Date.now() - previous.created_at < 86400000) return previous;
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  const request_id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  const receipt = { request_id, receipt_secret: base64url(window.crypto.getRandomValues(new Uint8Array(32))),
    owner_user_id: userId, created_at: Date.now() };
  // Both entropy and hashing must work before a destructive request is made.
  await receiptHash(receipt.receipt_secret);
  return saveReceipt(receipt);
}
export async function deletionRequest(path, body, { token, signal } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort(); else signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 25000);
  try {
    const response = await abortable(fetch(`${(process.env.REACT_APP_API_URL || '').replace(/\/$/, '')}${path}`, {
      method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(token ? { [config.token]: token } : {}) }, body: JSON.stringify(body),
    }), controller.signal);
    const value = await abortable(response.json(), controller.signal);
    if (!response.ok || !value?.deletion || value.deletion.request_id !== body.request_id) throw new Error('Deletion status unavailable');
    return value.deletion;
  } finally { clearTimeout(timeout); signal?.removeEventListener('abort', abort); }
}
export async function applyDeletionProof(receipt, status, token) {
  if (!validReceipt(receipt) || status?.request_id !== receipt.request_id || status.access_closed !== true) return;
  try { saveReceipt({ ...receipt, accepted: true }); } catch { /* Original proof already persisted before sending. */ }
  if (Number.isSafeInteger(receipt.owner_user_id)) {
    try { await cleanupDeletedOwner(receipt.owner_user_id, token); }
    catch { try { saveReceipt({ ...receipt, accepted: true, local_cleanup_pending: true }); } catch { /* local tombstone retries */ } }
    finally {
      try { if (getUser()?.id === receipt.owner_user_id && (!token || appLocalStorage.getItem(config.token) === token)) removeUser(); }
      catch { /* Remote access is closed; receipt remains usable when device storage is unavailable. */ }
    }
  }
}
export async function submitDeletion(receipt, token) {
  if (!validReceipt(receipt) || !token) throw new Error('Invalid request');
  const status = await deletionRequest('/user/deletion', { request_id: receipt.request_id, confirm: true,
    receipt_hash: await receiptHash(receipt.receipt_secret) }, { token });
  await applyDeletionProof(receipt, status, token);
  return status;
}
export const receiptFragment = receipt => `request_id=${encodeURIComponent(receipt.request_id)}&receipt_secret=${encodeURIComponent(receipt.receipt_secret)}`;
export function receiveReceipt() {
  const params = new URLSearchParams(window.location.hash.slice(1));
  if (!params.has('receipt_secret')) return readReceipt();
  // UUID spelling is case-insensitive; PostgreSQL and the API return lowercase.
  // Normalize imported links before local lookup and response correlation.
  const receipt = { request_id: params.get('request_id')?.toLowerCase(), receipt_secret: params.get('receipt_secret') };
  // Remove before any render, fetch, outbound link or analytics can see it.
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  if (!validReceipt(receipt)) return null;
  const known = readReceipt(receipt.request_id);
  const result = known?.receipt_secret === receipt.receipt_secret ? known : receipt;
  try { return saveReceipt(result); } catch { return result; }
}
